import React from "react";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatBRL } from "../InvoiceSplitPreviewModal";
import { displayCivilDate, displayWeeklyPeriod } from "../../../routes/pages/org.utils";
import type { WeeklyEconomicGroup, WeeklyStatementPage } from "../../../routes/pages/org.weekly";
import { NEGOTIATION_TOPUP_LABEL } from "./weeklyStatementLabels";
import "./WeeklyGroupedStatement.css";

type ServiceGroup = Extract<WeeklyEconomicGroup, { lineClass: "service" }>;

function groupLabel(group: ServiceGroup): string {
  const kind = group.kind.toLowerCase();
  const place =
    kind === "hall" ? " no Hall" : kind === "radio" ? " na rádio" : kind === "tv" ? " na TV" : "";
  if (group.metric === "view") return `Visualizações${place}`;
  // Verified Room admissions use access for the click metric on the wire.
  if (group.metric === "access") return `Cliques${place}`;
  if (group.negotiationInvoiceId && group.kind === "hall") return "Negociação no Hall";
  if (group.kind === "base-hall") return "Base diária do Hall";
  if (group.kind === "base-radio") return "Base diária da rádio";
  if (group.kind === "base-tv") return "Base diária da TV";
  if (group.kind === "hall") return "Hall";
  if (group.kind === "radio") return "Rádio";
  if (kind === "tv") return "TV";
  return "Serviço registrado";
}

export type WeeklyGroupedStatementProps = { page: WeeklyStatementPage };

/**
 * Financial groups are already summed and paginated by Payments. A count is
 * shown only when the immutable source proves its metric; there is no repricing.
 * @story layout padded
 * @story args { page: { invoiceId: "weekly", invoiceStatus: "weekly-open", totalPages: 1, summary: { serviceAmountCents: 1014, totalAmountCents: 1014, groupCount: 2 }, groups: [{ groupId: "hall", lineClass: "service", kind: "hall", amountCents: 1000, negotiationInvoiceId: "negotiation", weeklyLimit: { mode: "unlimited" }, weeklyLimitVersion: 1 }, { groupId: "base", lineClass: "service", kind: "base-hall", amountCents: 14, monthlyBaseCents: 220, proration: "monthly-cumulative-floor-v1", serviceDates: ["2026-10-05", "2026-10-06"], dailyAmounts: [{ serviceDate: "2026-10-05", amountCents: 7 }, { serviceDate: "2026-10-06", amountCents: 7 }], weeklyLimit: { mode: "unlimited" }, weeklyLimitVersion: 1 }] } }
 */
export function WeeklyGroupedStatement({ page }: WeeklyGroupedStatementProps) {
  const groupRegion = React.useId();
  const services = page.groups.filter(
    (group): group is ServiceGroup => group.lineClass === "service",
  );
  return (
    <div className="weeklyGroupedStatement" data-testid="weekly-grouped-statement">
      <dl className="totals">
        {page.weeklyBilling?.disposition === "accumulation" ? (
          <>
            <div>
              <dt>Serviços desta semana</dt>
              <dd>{formatBRL(page.weeklyBilling.ownServiceAmountCents)}</dd>
            </div>
            {page.weeklyBilling.carriedServiceAmountCents > 0 && (
              <div>
                <dt>Serviços de semanas anteriores</dt>
                <dd>{formatBRL(page.weeklyBilling.carriedServiceAmountCents)}</dd>
              </div>
            )}
          </>
        ) : (
          <div>
            <dt>Serviços da semana</dt>
            <dd>{formatBRL(page.summary.serviceAmountCents)}</dd>
          </div>
        )}
        {page.summary.processingFeeAmountCents !== undefined && (
          <div>
            <dt>Tarifa fixa Pix</dt>
            <dd data-testid="weekly-pix-fee-amount">
              {formatBRL(page.summary.processingFeeAmountCents)}
            </dd>
          </div>
        )}
        {page.summary.customerCreditAmountCents !== undefined && (
          <div>
            <dt>Crédito da empresa</dt>
            <dd data-testid="weekly-pix-customer-credit-amount">
              {formatBRL(page.summary.customerCreditAmountCents)}
            </dd>
          </div>
        )}
        {page.summary.negotiationTopupAmountCents !== undefined && (
          <div>
            <dt>{NEGOTIATION_TOPUP_LABEL}</dt>
            <dd data-testid="weekly-pix-negotiation-topup-amount">
              {formatBRL(page.summary.negotiationTopupAmountCents)}
            </dd>
          </div>
        )}
        {page.weeklyBilling?.eligibleCustomerCreditAmountCents !== undefined && (
          <div>
            <dt>Crédito elegível, a conferir na preparação</dt>
            <dd>{formatBRL(page.weeklyBilling.eligibleCustomerCreditAmountCents)}</dd>
          </div>
        )}
        {page.weeklyBilling?.eligibleCustomerCreditAmountCents !== undefined &&
          page.weeklyBilling.netServiceAmountCents !== undefined && (
            <div>
              <dt>Serviços líquidos previstos, antes da tarifa</dt>
              <dd>{formatBRL(page.weeklyBilling.netServiceAmountCents)}</dd>
            </div>
          )}
      </dl>
      {page.weeklyBilling?.eligibleCustomerCreditAmountCents !== undefined && (
        <p>O crédito elegível é uma previsão e ainda não foi aplicado a esta cobrança.</p>
      )}
      <h3>Composição dos serviços</h3>
      {services.length === 0 && (
        <p className="empty">
          {page.groups.length === 0
            ? "Ainda não há serviços registrados nesta página."
            : page.groups.some((group) => group.lineClass === "negotiation_topup")
              ? "A tarifa, o crédito e a recarga do Saldo de Negociações desta página estão exibidos no resumo acima."
              : "A tarifa e o crédito desta página estão exibidos no resumo acima."}
        </p>
      )}
      {services.map((group, index) => (
        <Accordion className="group" key={group.groupId} disableGutters elevation={0}>
          <AccordionSummary
            id={`${groupRegion}-${index}`}
            aria-controls={`${groupRegion}-details-${index}`}
            expandIcon={<ExpandMoreIcon />}
          >
            <span className="groupLabel">
              <span>{groupLabel(group)}</span>
              {group.originPeriod && (
                <small>Semana de {displayWeeklyPeriod(group.originPeriod)}</small>
              )}
              {group.metric && group.occurrenceCount !== undefined && (
                <small>
                  {group.occurrenceCount.toLocaleString("pt-BR")}{" "}
                  {group.metric === "view"
                    ? group.occurrenceCount === 1
                      ? "visualização"
                      : "visualizações"
                    : group.occurrenceCount === 1
                      ? "clique"
                      : "cliques"}
                </small>
              )}
              {group.serviceDates?.length ? (
                <small>
                  {group.baseSourceCount !== undefined &&
                    `${group.baseSourceCount.toLocaleString("pt-BR")} ${group.baseSourceCount === 1 ? "diária registrada" : "diárias registradas"} em `}
                  {group.serviceDates.length}{" "}
                  {group.baseSourceCount !== undefined
                    ? group.serviceDates.length === 1
                      ? "dia"
                      : "dias"
                    : group.serviceDates.length === 1
                      ? "dia registrado"
                      : "dias registrados"}
                </small>
              ) : null}
            </span>
            <strong className="amount" data-testid="weekly-service-group-amount">
              {formatBRL(group.amountCents)}
            </strong>
          </AccordionSummary>
          <AccordionDetails className="groupDetails">
            {group.baseSourceCount !== undefined && (
              <p>Uma diária corresponde a um anúncio em um dia.</p>
            )}
            {group.monthlyBaseCents !== undefined && (
              <p>
                Tarifa mensal por anúncio: {formatBRL(group.monthlyBaseCents)}.
                {group.proration === "monthly-cumulative-floor-v1"
                  ? " Rateio diário com os centavos distribuídos ao longo do mês."
                  : ""}
              </p>
            )}
            {group.dailyAmounts ? (
              <dl className="dailyAmounts">
                {group.dailyAmounts.map((daily) => (
                  <div key={daily.serviceDate}>
                    <dt>{displayCivilDate(daily.serviceDate)}</dt>
                    <dd>
                      {daily.sourceCount !== undefined &&
                        `${daily.sourceCount.toLocaleString("pt-BR")} ${daily.sourceCount === 1 ? "diária" : "diárias"} · `}
                      {formatBRL(daily.amountCents)}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : group.serviceDates?.length ? (
              <p>Dias registrados: {group.serviceDates.map(displayCivilDate).join(", ")}.</p>
            ) : null}
            {group.weeklyLimit && (
              <p>
                {group.weeklyLimit.mode === "fixed"
                  ? `Limite aplicado a estes serviços: ${formatBRL(group.weeklyLimit.cents)}.`
                  : "Estes serviços foram registrados sem teto interno da DaKasa."}
              </p>
            )}
            {group.metric && <p>O valor é o total registrado para este grupo.</p>}
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
}
